import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

export default function Notifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.get('/notifications?limit=100', token);
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [token]);

  const markRead = async (id) => {
    try {
      setWorkingId(id);
      await api.patch(`/notifications/${id}/read`, {}, token);
      setStatus('Notification marked read');
      await loadNotifications();
    } catch (err) {
      setError(err.message || 'Failed to mark notification read');
    } finally {
      setWorkingId(null);
    }
  };

  const markAllRead = async () => {
    try {
      setWorkingId('all-read');
      await api.patch('/notifications/read-all', {}, token);
      setStatus('All notifications marked read');
      await loadNotifications();
    } catch (err) {
      setError(err.message || 'Failed to mark all read');
    } finally {
      setWorkingId(null);
    }
  };

  const removeNotification = async (id) => {
    try {
      setWorkingId(id);
      await api.delete(`/notifications/${id}`, token);
      setStatus('Notification removed');
      await loadNotifications();
    } catch (err) {
      setError(err.message || 'Failed to remove notification');
    } finally {
      setWorkingId(null);
      setConfirmModal(null);
    }
  };

  const clearReadNotifications = async () => {
    try {
      setWorkingId('clear-read');
      const result = await api.delete('/notifications/read', token);
      setStatus(`Removed ${result.removed || 0} read notification(s)`);
      await loadNotifications();
    } catch (err) {
      setError(err.message || 'Failed to clear read notifications');
    } finally {
      setWorkingId(null);
      setConfirmModal(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="mt-1 text-sm text-slate-500">Operational alerts and storefront activity.</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            className="btn-primary"
            onClick={markAllRead}
            disabled={Boolean(workingId)}
          >
            {workingId === 'all-read' ? 'Working...' : 'Mark All Read'}
          </button>

          <button
            className="btn-danger-soft"
            onClick={() =>
              setConfirmModal({
                title: 'Clear read notifications?',
                message: 'This permanently removes all notifications already marked as read.',
                confirmLabel: 'Clear read',
                destructive: true,
                onConfirm: clearReadNotifications
              })
            }
            disabled={Boolean(workingId)}
          >
            {workingId === 'clear-read' ? 'Working...' : 'Clear Read'}
          </button>
        </div>
      </div>

      {error ? <div className="status-banner-error">{error}</div> : null}
      {status ? <div className="status-banner-success">{status}</div> : null}

      {loading ? (
        <div className="card p-6 loading-pulse">Loading notifications...</div>
      ) : null}

      <div className="space-y-3">
        {notifications.map((notification) => (
          <div key={notification.id} className="card-interactive p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold">{notification.title}</h3>
                  {!notification.is_read ? (
                    <span className="badge badge-orange">Unread</span>
                  ) : (
                    <span className="badge badge-green">Read</span>
                  )}
                </div>
                <p className="mt-2 text-slate-600">{notification.message}</p>
                <p className="mt-3 text-sm text-slate-400">
                  {new Date(notification.created_at).toLocaleString()}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                {!notification.is_read ? (
                  <button
                    className="btn-accent px-4 py-2 text-sm"
                    onClick={() => markRead(notification.id)}
                    disabled={workingId === notification.id}
                  >
                    {workingId === notification.id ? 'Working...' : 'Mark Read'}
                  </button>
                ) : null}

                <button
                  className="btn-danger-soft px-4 py-2 text-sm"
                  onClick={() =>
                    setConfirmModal({
                      title: 'Remove notification?',
                      message: 'This notification will be permanently removed from the admin list.',
                      confirmLabel: 'Remove',
                      destructive: true,
                      onConfirm: () => removeNotification(notification.id)
                    })
                  }
                  disabled={workingId === notification.id}
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}

        {!notifications.length && !loading ? (
          <div className="card p-6">No notifications yet.</div>
        ) : null}
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        destructive={confirmModal?.destructive}
        loading={Boolean(workingId)}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}
